import React, { useState, useEffect } from 'react';
import './ReferralForm.css';

export default function ReferralForm() {
  const [formData, setFormData] = useState({
    userFirstName: '',
    userLastName: '',
    userEmail: ''
  });
  const [currentJournalist, setCurrentJournalist] = useState({
    firstName: '',
    lastName: '',
    email: '',
    publication: ''
  });
  const [savedJournalists, setSavedJournalists] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isReferralsExpanded, setIsReferralsExpanded] = useState(false);
  const [utmParams, setUtmParams] = useState({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    gclid: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Utility function to read a cookie by name
  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Capture UTM parameters and GCLID from parent page via postMessage
  useEffect(() => {
    const captureUtmParams = () => {
      let params = {
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: '',
        utm_term: '',
        gclid: ''
      };

      console.log('=== UTM/GCLID Capture Debug ===');

      // First, try to get from current URL
      const urlParams = new URLSearchParams(window.location.search);
      params = {
        utm_source: urlParams.get('utm_source') || '',
        utm_medium: urlParams.get('utm_medium') || '',
        utm_campaign: urlParams.get('utm_campaign') || '',
        utm_content: urlParams.get('utm_content') || '',
        utm_term: urlParams.get('utm_term') || '',
        gclid: urlParams.get('gclid') || ''
      };

      console.log('UTM/GCLID params from iframe URL:', params);

      // If not in URL, check cookies
      if (!params.utm_source) {
        const cookieValue = getCookie('utm_source');
        console.log('utm_source from cookie:', cookieValue);
        params.utm_source = cookieValue || '';
      }
      if (!params.utm_medium) {
        const cookieValue = getCookie('utm_medium');
        console.log('utm_medium from cookie:', cookieValue);
        params.utm_medium = cookieValue || '';
      }
      if (!params.utm_campaign) {
        const cookieValue = getCookie('utm_campaign');
        console.log('utm_campaign from cookie:', cookieValue);
        params.utm_campaign = cookieValue || '';
      }
      if (!params.utm_content) {
        const cookieValue = getCookie('utm_content');
        console.log('utm_content from cookie:', cookieValue);
        params.utm_content = cookieValue || '';
      }
      if (!params.utm_term) {
        const cookieValue = getCookie('utm_term');
        console.log('utm_term from cookie:', cookieValue);
        params.utm_term = cookieValue || '';
      }
      if (!params.gclid) {
        const cookieValue = getCookie('gclid');
        console.log('gclid from cookie:', cookieValue);
        params.gclid = cookieValue || '';
      }

      console.log('Final UTM/GCLID params:', params);
      console.log('=== End UTM/GCLID Capture Debug ===');

      setUtmParams(params);
    };

    // Listen for UTM data from parent page via postMessage
    const handleParentMessage = (event) => {
      console.log('[Iframe] Received postMessage:', event.data);

      if (event.data?.type === 'PARENT_PAGE_DATA') {
        console.log('[Iframe] Processing parent page data...');

        const receivedParams = {
          utm_source: event.data.utm_source || '',
          utm_medium: event.data.utm_medium || '',
          utm_campaign: event.data.utm_campaign || '',
          utm_content: event.data.utm_content || '',
          utm_term: event.data.utm_term || '',
          gclid: event.data.gclid || ''
        };

        console.log('[Iframe] UTM/GCLID params from parent:', receivedParams);

        // Only update if we received at least one parameter
        if (receivedParams.utm_source || receivedParams.utm_medium || receivedParams.utm_campaign ||
            receivedParams.utm_content || receivedParams.utm_term || receivedParams.gclid) {
          setUtmParams(receivedParams);
        }
      }
    };

    window.addEventListener('message', handleParentMessage);

    // Also capture from URL/cookies on initial load
    captureUtmParams();

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleJournalistChange = (field, value) => {
    setCurrentJournalist(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addJournalist = () => {
    if (currentJournalist.firstName && currentJournalist.lastName && currentJournalist.email && currentJournalist.publication) {
      if (editingIndex !== null) {
        // Update existing journalist
        const updatedJournalists = [...savedJournalists];
        updatedJournalists[editingIndex] = currentJournalist;
        setSavedJournalists(updatedJournalists);
        setEditingIndex(null);
      } else {
        // Add new journalist
        setSavedJournalists(prev => [...prev, currentJournalist]);
      }
      // Clear the form
      setCurrentJournalist({ firstName: '', lastName: '', email: '', publication: '' });
    }
  };

  const editJournalist = (index) => {
    setCurrentJournalist(savedJournalists[index]);
    setEditingIndex(index);
    setIsReferralsExpanded(false);
  };

  const deleteJournalist = (index) => {
    setSavedJournalists(prev => prev.filter((_, i) => i !== index));
  };

  const cancelEdit = () => {
    setCurrentJournalist({ firstName: '', lastName: '', email: '', publication: '' });
    setEditingIndex(null);
  };

  // Check if current journalist form is complete
  const isCurrentJournalistComplete =
    currentJournalist.firstName &&
    currentJournalist.lastName &&
    currentJournalist.email &&
    currentJournalist.publication;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Build the journalists array - include saved journalists and current journalist if complete
    let journalistsToSubmit = [...savedJournalists];
    if (isCurrentJournalistComplete && editingIndex === null) {
      // Add current journalist if it's filled out and not being edited
      journalistsToSubmit.push(currentJournalist);
    }

    try {
      const response = await fetch('/.netlify/functions/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userFirstName: formData.userFirstName,
          userLastName: formData.userLastName,
          userEmail: formData.userEmail,
          journalists: journalistsToSubmit,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_content: utmParams.utm_content,
          utm_term: utmParams.utm_term,
          gclid: utmParams.gclid
        })
      });

      if (response.ok) {
        setSubmitSuccess(true);

        // Send dataLayer event to parent page for GTM tracking
        if (window.parent) {
          const dataLayerEvent = {
            type: 'DATALAYER_PUSH',
            event: 'invite_to_qwoted_form_submit',
            data: {
              form_name: 'Invite to Qwoted Referral Form',
              user_email: formData.userEmail,
              user_first_name: formData.userFirstName,
              user_last_name: formData.userLastName,
              journalist_count: journalistsToSubmit.length,
              utm_source: utmParams.utm_source,
              utm_medium: utmParams.utm_medium,
              utm_campaign: utmParams.utm_campaign,
              utm_content: utmParams.utm_content,
              utm_term: utmParams.utm_term,
              gclid: utmParams.gclid
            }
          };

          console.log('[Iframe] Sending dataLayer event to parent:', dataLayerEvent);
          window.parent.postMessage(dataLayerEvent, '*');
        }

        // Reset form after 2 seconds
        setTimeout(() => {
          setFormData({
            userFirstName: '',
            userLastName: '',
            userEmail: ''
          });
          setCurrentJournalist({ firstName: '', lastName: '', email: '', publication: '' });
          setSavedJournalists([]);
          setEditingIndex(null);
          setIsReferralsExpanded(false);
          setSubmitSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if form can be submitted (user info + either saved journalists OR current journalist complete)
  const canSubmit = formData.userFirstName && formData.userLastName && formData.userEmail && (savedJournalists.length > 0 || isCurrentJournalistComplete);

  if (submitSuccess) {
    return (
      <div className="referral-form-container">
        <div className="success-message">
          <h2>Thank you!</h2>
          <p>Your referral has been submitted successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-form-container">
      <form onSubmit={handleSubmit} className="referral-form">
        <h2>Invite a Journalist</h2>

        <div className="form-content">
          {/* Your Information Section */}
          <div className="form-section">
            <h3>Your Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="userFirstName">First Name</label>
                <input
                  type="text"
                  id="userFirstName"
                  name="userFirstName"
                  value={formData.userFirstName}
                  onChange={handleInputChange}
                  placeholder="First name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="userLastName">Last Name</label>
                <input
                  type="text"
                  id="userLastName"
                  name="userLastName"
                  value={formData.userLastName}
                  onChange={handleInputChange}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="userEmail">Email</label>
              <input
                type="email"
                id="userEmail"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleInputChange}
                placeholder="Email"
                required
              />
            </div>
          </div>

          {/* Your Referrals Section - Always visible to maintain consistent height */}
          <div className="referrals-section">
            <div
              className="referrals-header"
              onClick={() => savedJournalists.length > 0 && setIsReferralsExpanded(!isReferralsExpanded)}
              style={{ cursor: savedJournalists.length > 0 ? 'pointer' : 'default' }}
            >
              <h3>Your Referrals ({savedJournalists.length})</h3>
              {savedJournalists.length > 0 && (
                <span className={`arrow ${isReferralsExpanded ? 'expanded' : ''}`}>▼</span>
              )}
            </div>

            {isReferralsExpanded && savedJournalists.length > 0 && (
              <div className="referrals-list">
                {savedJournalists.map((journalist, index) => (
                  <div key={index} className="referral-item">
                    <div className="referral-info">
                      <span className="referral-name">{journalist.firstName} {journalist.lastName}</span>
                      <span className="referral-email">{journalist.email}</span>
                    </div>
                    <div className="referral-actions">
                      <button
                        type="button"
                        onClick={() => editJournalist(index)}
                        className="icon-btn edit-btn"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteJournalist(index)}
                        className="icon-btn delete-btn"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Journalist Information Section */}
          <div className="form-section">
            <h3>Journalist Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="journalist-firstName">First Name</label>
                <input
                  type="text"
                  id="journalist-firstName"
                  value={currentJournalist.firstName}
                  onChange={(e) => handleJournalistChange('firstName', e.target.value)}
                  placeholder="First name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="journalist-lastName">Last Name</label>
                <input
                  type="text"
                  id="journalist-lastName"
                  value={currentJournalist.lastName}
                  onChange={(e) => handleJournalistChange('lastName', e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="journalist-email">Email</label>
              <input
                type="email"
                id="journalist-email"
                value={currentJournalist.email}
                onChange={(e) => handleJournalistChange('email', e.target.value)}
                placeholder="Email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="journalist-publication">Referral Publication/Organization</label>
              <input
                type="text"
                id="journalist-publication"
                value={currentJournalist.publication}
                onChange={(e) => handleJournalistChange('publication', e.target.value)}
                placeholder="Publication or Organization"
              />
            </div>

            <div className="journalist-actions">
              {editingIndex !== null && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={addJournalist}
                className="btn-secondary add-btn"
                disabled={!isCurrentJournalistComplete}
              >
                {editingIndex !== null ? 'Update Referral' : '+ Add Another Journalist'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-primary submit-btn"
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Invite to Qwoted'}
          </button>
        </div>
      </form>
    </div>
  );
}
